import { cn } from "~/lib/utils";
import { Tooltip } from "~/components/Tooltip";

// // // //

type AssetRating = "moodys_aaa" | "fitch_aaammf" | "sp_aaam";

type RatingConfig = {
    // Name of the ratings agency (left side of the badge)
    agency: string;
    // The rating itself (right side of the badge)
    rating: string;
    // Full rating label, displayed in the tooltip
    tooltipLabel: string;
    // Description of the rating, displayed in the tooltip
    description: string;
};

const RATING_CONFIGS: Record<AssetRating, RatingConfig> = {
    moodys_aaa: {
        agency: "Moody's",
        rating: "Aaa-mf",
        tooltipLabel: "Moody's Aaa-mf",
        description:
            "Very strong ability to meet the dual objectives of providing liquidity and preserving capital.",
    },
    fitch_aaammf: {
        agency: "Fitch",
        rating: "AAAmmf",
        tooltipLabel: "Fitch AAAmmf",
        description:
            "Strongest capacity to achieve the investment objective of preserving capital and providing liquidity through limited credit market and liquidity risk.",
    },
    sp_aaam: {
        agency: "S&P",
        rating: "AAAm",
        tooltipLabel: "S&P Global Ratings AAAm",
        description:
            "Denotes creditworthiness of the fund's investments and counterparties, the investments' maturity structure, and management's ability and policies to maintain the fund's stable net asset value.",
    },
};

export function AssetRatingBadge(props: { rating: AssetRating }) {
    const config = RATING_CONFIGS[props.rating];

    if (!config) {
        return null;
    }

    const containerClassName = "bg-[#0b1a56]/90 border-[#1e417b]/90 text-white";
    const ratingClassName = "bg-white text-[#0b1a56]/90";

    return (
        <Tooltip
            placement="right"
            tooltipContent={
                <div className="px-3 py-2 font-light max-w-sm flex flex-col gap-1 text-left">
                    <p className="text-sm whitespace-nowrap text-left mb-1">
                        {config.tooltipLabel}
                    </p>
                    <p className="text-xs mt-1">{config.description}</p>
                </div>
            }
        >
            {({ setTriggerRef }) => (
                <div
                    ref={setTriggerRef}
                    className={cn(
                        "w-min whitespace-nowrap overflow-hidden max-w-full cursor-default flex items-center gap-1 text-xs rounded-sm border",
                        containerClassName,
                    )}
                >
                    <span className="pl-2 pr-1 py-0.5 font-light">
                        {config.agency}
                    </span>
                    <span
                        className={cn(
                            "py-0.5 px-1.5 pr-2 flex items-center gap-0.5 font-normal",
                            ratingClassName,
                        )}
                    >
                        {config.rating}
                    </span>
                </div>
            )}
        </Tooltip>
    );
}
